class VariantSelector extends HTMLElement {
	constructor() {
		super();
		this.dropdownsOpen = false;

		this.addEventListener('optionchange', e => this.updateSelected(e.detail.trusted));

		let preorderPopup = this.querySelector('.variant-selector__preorder-popup');

		preorderPopup.querySelector('.preorder-popup__back').addEventListener('click', () => this.closePreorderPopup());

		preorderPopup.querySelector('.preorder-popup__atc').addEventListener('click', async e => {
			let btn = e.currentTarget;
			btn.classList.add('show-loading');
			if (this.preorderAtc) await this.preorderAtc();
			btn.classList.remove('show-loading');
			this.closePreorderPopup();
		});

		preorderPopup.addEventListener('click', e => {
			// Close if clicking on popup (ie, the background) instead of its contents 
			if (e.target === e.currentTarget) {
				this.closePreorderPopup();
			}
		});

		this.addEventListener('optionclick', e => {
			if (!this.dropdownsOpen && this.dataset.preorderPopup == 'true' && e.detail.trusted && e.detail.preorder && window.innerWidth < 992) this.showPreorderPopup();
		});

		window.addEventListener('growthbookready', () => this.firePriceChangedEvent());
	}

	async connectedCallback() {
		this.firePriceChangedEvent();

		if (this.hasAttribute('lazyload')) {
			const url = new URL(window.location.href);
			url.searchParams.set('view', 'variant-selector-data');

			const resp = await fetch(url);
			const text = await resp.text();
			const doc = new DOMParser().parseFromString(text, 'text/html');

			const newData = doc.querySelector('select[name="id"]');
			const existingData = this.querySelector('select[name="id"]');
			existingData.replaceWith(newData);

			this.removeAttribute('lazyload');
		}

		this.updateSelected();
	}

	firePriceChangedEvent() {
		if (!this.parentElement || !this.parentElement.classList.contains('product-form')) return;
		const select = this.querySelector('select');
		const variant = select.selectedOptions[0].dataset;
		const priceRaw = variant.priceTestPrices && window.getActivePriceTest() ? JSON.parse(variant.priceTestPrices)[window.getActivePriceTestIndex()] : variant.price;
		const price = parseFloat(priceRaw) / 100;
		let event = new CustomEvent('pricechanged', { detail: { price: price } });
		window.dispatchEvent(event);
	}

	updateSelected(trusted = false) {
		const select = this.querySelector('select');
		const optionSelectors = this.querySelectorAll('option-selector');
		const attrs = [];

		for (const selector of optionSelectors) {
			if (!selector.dataset.selected) return;
			attrs.push(`[data-option-${selector.dataset.option}="${selector.dataset.selected}"]`);
		}

		const variant = select.querySelector(`option${attrs.join('')}`);

		if (variant) {
			variant.selected = true;
			
			this.querySelectorAll('.variant-selector__price').forEach(elem => elem.innerHTML = variant.dataset.priceTextShort);
			this.dataset.preorder = variant.dataset.status == 'preorder';

			this.querySelector('.preorder-popup__title').innerHTML = variant.dataset.title;
			this.querySelector('.preorder-popup__month').innerHTML = variant.dataset.preorderMonth;
			let bisForm = this.querySelector('bis-form');
			if (bisForm) bisForm.setAttribute('variant', variant.dataset.id);

			const inclusions = this.querySelector('.variant-selector__inclusions');
			if (inclusions) inclusions.innerHTML = variant.dataset.inclusions;

			const mixedReason = this.querySelector('.variant-selector__mixed-reason');
			if (mixedReason) mixedReason.innerHTML = variant.dataset.mixedReason;

			const variantObj = {
				...variant.dataset,
				isBundle: variant.dataset.isBundle == 'true',
				addToCartEnabled: variant.dataset.addToCartEnabled == 'true',
				price: parseInt(variant.dataset.price)
			}

			if (this.dataset.selected && this.dataset.selected != variant.value) {
				select.dispatchEvent(new Event('change'));

				this.dispatchEvent(new CustomEvent('variantchange', { 
					detail: { 
						variant: variantObj,
						trusted: trusted
					}, bubbles: true 
				}));

				document.dispatchEvent(new CustomEvent('variant:change', { 
					detail: { 
						variant: variantObj,
						trusted: trusted
					}, bubbles: true 
				}));

				this.firePriceChangedEvent();
			}

			this.selectedVariant = variantObj;
			this.dataset.selected = variant.value;
		}
	}

	async addToCart(quantity = 1, properties = {}) {
		const error = await addToCartGlobal(
			this.selectedVariant.productHandle,
			this.selectedVariant.id,
			quantity,
			this.selectedVariant.isBundle,
			this.selectedVariant.status == 'preorder' ? this.selectedVariant.preorderMonth : null,
			properties
		);

		if (!error) {
			await window.updateCartDrawer();
			window.showCartDrawer(true);

			const nostoElem = this.closest('.nosto_element');
			if (nostoElem && typeof nostojs == 'function') {
				// Nosto attributes conversions to the recommendation's attribution key, not the
				// placement element id — fall back to the element id for campaigns that don't set it
				const attributionElem = this.closest('[attribution-key]');
				const attributionKey = attributionElem && attributionElem.getAttribute('attribution-key') || nostoElem.id;

				nostojs(api => {
					api.recommendedProductAddedToCart(this.selectedVariant.productId, attributionKey);
				});
			}
		}
	}

	async triggerDropdowns(force = false) {
		this.dropdownsOpen = true;
		for (let selector of this.querySelectorAll('option-selector')) {
			if (!selector.dataset.selected || force) {
				let selected = await selector.openDropdown();

				if (!selected) {
					this.dropdownsOpen = false;
					return false;
				} 
			}
		}

		this.dropdownsOpen = false;

		if (this.dataset.preorderPopup == 'true' && this.dataset.preorder == 'true') {
			await this.showPreorderPopup();
			return false;
		}
		else return true;
	}

	async showPreorderPopup() {
		let popup = this.querySelector('.variant-selector__preorder-popup');
		popup.hidden = false;
		popup.classList.add('transition-in');
		document.documentElement.classList.add('modal-open');
		setTimeout(() => popup.classList.remove('transition-in'), 300);

		return new Promise(resolve => {
			this.addEventListener('preorderpopupclose', resolve, { once: true });
		});
	}

	closePreorderPopup() {
		let popup = this.querySelector('.variant-selector__preorder-popup');
		popup.hidden = true;
		popup.classList.add('transition-out');
		document.documentElement.classList.remove('modal-open');
		setTimeout(() => popup.classList.remove('transition-out'), 300);
		this.dispatchEvent(new CustomEvent('preorderpopupclose'));
	}
}

class OptionSelector extends HTMLElement {
	constructor() {
		super();

		let fieldset = this.querySelector('fieldset');

		fieldset.addEventListener('change', e => {
			this.updateSelected();
			this.dispatchEvent(new CustomEvent('optionchange', { bubbles: true, detail: { trusted: e.isTrusted }}));
			if (this.dataset.preorder != 'true' || this.dataset.closeOnPreorder != 'false') this.closeDropdown();
		});
		
		for (let radio of fieldset.querySelectorAll('.option-selector__button input[type="radio"]')) {
			radio.addEventListener('click', e => {
				if (radio.checked) {
					this.dispatchEvent(new CustomEvent('optionclick', { bubbles: true, detail: { trusted: e.isTrusted, preorder: radio.dataset.preorder == 'true' }}));
					if (this.dataset.preorder != 'true' || this.dataset.closeOnPreorder != 'false') this.closeDropdown();
				}

				if (window.trackFSEvent) {
					window.trackFSEvent('PDP Option Click', {
						value: radio.value,
						name: radio.name,
						preorder: radio.dataset.preorder == 'true',
						lowstock: radio.dataset.lowstock == 'true',
						available: radio.dataset.available == 'true',
						soldout: radio.dataset.soldout == 'true',
					});
				}
			});
		};

		this.dropdownToggle = this.querySelector('button[name="select-option"]');
		this.dropdownToggle.addEventListener('click', () => this.toggleDropdown());

		this.registerDragLogic();
		this.closeOnClickOutHandler = this.closeOnClickOut.bind(this);
	}

	toggleDropdown() {
		if (this.dataset.dropdownOpen == 'true') this.closeDropdown();
		else this.openDropdown();
	}

	closeDropdown() {
		if (this.dataset.dropdownOpen == 'false') return;
		this.dataset.dropdownOpen = 'false';
		document.body.classList.remove('option-selector-open');
		this.classList.add('transition-out');
		this.dispatchEvent(new CustomEvent('dropdownclose', { bubbles: true }));
		window.removeEventListener('click', this.closeOnClickOutHandler);
		setTimeout(() => {
			this.classList.remove('transition-out');
			this.querySelector('fieldset').style.removeProperty('--offset');
		}, 300);
	}

	closeOnClickOut(e) {
		if (document.documentElement.classList.contains('modal-open')) return;
		if (this.dataset.dropdownOpen == 'false') return;

		let fieldset = this.querySelector('fieldset');

		let sizeGuide = document.querySelector('.size-guide-container');
		if (sizeGuide && sizeGuide.contains(e.target)) return;
		
		if (!this.contains(e.target) || (e.target.name != 'select-option' && !fieldset.contains(e.target))) {
			this.closeDropdown();
			this.dispatchEvent(new CustomEvent('cancel'));
			window.removeEventListener('click', this.closeOnClickOutHandler);
		}
	}

	async openDropdown() {
		if (this.dataset.dropdownOpen == 'true') return;
		this.dataset.dropdownOpen = 'true';
		document.body.classList.add('option-selector-open');
		this.classList.add('transition-in');
		this.dispatchEvent(new CustomEvent('dropdownopen', { bubbles: true }));

		if (window.innerWidth > 992) {
			setTimeout(() => {
				const fieldset = this.querySelector('fieldset');
				const bounds = fieldset.getBoundingClientRect();
				const scrollableBounds = this.closest('ul')?.getBoundingClientRect();
				const offset = Math.min((scrollableBounds ? scrollableBounds.right : window.innerWidth) - bounds.right - 16, 0);
				fieldset.style.setProperty('--offset', `${offset}px`);
			})
		}

		setTimeout(() => {
			this.classList.remove('transition-in');
			window.addEventListener('click', this.closeOnClickOutHandler);
		}, 300);

		return await new Promise(resolve => {
			this.addEventListener('optionclick', () => setTimeout(() => resolve(true), 100), { once: true });
			this.addEventListener('optionchange', () => resolve(true), { once: true });
			this.addEventListener('cancel', () => resolve(false), { once: true });
		});
	}

	updateSelected() {
		let checked = this.querySelector('.option-selector__buttons input:checked');
		if (checked) {
			console.log(checked);
			this.dataset.selected = checked.value;
			this.dataset.mixed = checked.value.includes('Special');
			this.dataset.preorder = checked.dataset.preorder == 'true' ? 'true' : 'false';
			this.dataset.lowstock = checked.dataset.lowstock == 'true' ? 'true' : 'false';
			this.dataset.available = checked.dataset.available == 'true' ? 'true' : 'false';
			this.dataset.soldout = checked.dataset.soldout == 'true' ? 'true' : 'false';

      let label = checked.value.split('(')[0].trim();

			// Change the main text of the variant selector
      this.dropdownToggle.querySelector(
        '.select-option__main-text'
      ).textContent = label.includes('Special') ? `${label}*` : label;

			// Change the stock availability text
			if (checked.dataset.preorder == 'true') {
        this.dropdownToggle.querySelector(
          '.select-option__availability-text'
        ).textContent = 'Pre-Order';
      } else if (checked.dataset.lowstock == 'true') {
        this.dropdownToggle.querySelector(
          '.select-option__availability-text'
        ).textContent = 'Low stock';
      } else if (checked.dataset.available == 'true') {
        this.dropdownToggle.querySelector(
          '.select-option__availability-text'
					).textContent = 'In stock';
			} else if (checked.dataset.soldout == 'true') {
        this.dropdownToggle.querySelector(
          '.select-option__availability-text'
					).textContent = checked.dataset.restocking == 'true' ? 'Restocking' : 'Sold out';
			}
    }
	}

	registerDragLogic() {
		let popup = this.querySelector('fieldset');
		let dragging = false;
		let dragStart = 0;
		let offset = 0;

		// Drag to close logic for mobile
		popup.addEventListener('touchstart', e => {
			let touchPos = e.touches[0].clientY - popup.offsetTop;
			if (touchPos >= 0 && touchPos <= 40) {
				dragging = true;
				dragStart = touchPos;
			}
		});

		popup.addEventListener('touchmove', e => {
			if (dragging) {
				popup.style.transition = '';
				let touchPos = e.touches[0].clientY - popup.offsetTop;
				offset = touchPos - dragStart;
				popup.style.setProperty('--offset', `${Math.max(0, offset)}px`);
			}
		});

		popup.addEventListener('touchend', e => {
			if (dragging) {
				dragging = false;

				// Close dialog if user has moved it far down enough
				if (offset / popup.clientHeight > 0.2) {
					popup.classList.add('swipe-out');

					this.closeDropdown();
					this.dispatchEvent(new CustomEvent('cancel'));

					setTimeout(() => {
						popup.classList.remove('swipe-out');
						popup.style.removeProperty('--offset');
					}, 500);
				}
				// Otherwise, move it back up
				else {
					popup.style.transition = 'transform 250ms';
					popup.style.removeProperty('--offset');
				}

				dragStart = 0;
				offset = 0;
			}
		});
	}
}

customElements.define('variant-selector', VariantSelector);
customElements.define('option-selector', OptionSelector);